/**
 * 双面翻牌（ADR-0011，design §11.6）：照片面板里的这张卡，正反两面各显示什么。
 * 正面（A 面）是上传这张照片的人写的，背面（B 面）是另一个人写的；每人只能写自己那面。
 * 视角是服务端按你能看到的给的：写前互盲开着、你还没写这张照片时，Ta 那面只有 hidden、没有字。
 */
import type { PerspectiveDto, VoiceDto } from '@star-sea/shared';

export interface CardInput {
  me: string;
  myName: string;
  /** 上传这张照片的人 */
  uploader: string;
  /** 另一个人；还没加入星海时是 null */
  partner: { id: string; name: string } | null;
  /** 这张照片上的视角（按你能看到的） */
  perspectives: readonly PerspectiveDto[];
  voices?: readonly VoiceDto[];
}

export interface CardFace {
  /** 这一面是谁的；还没有 Ta 时背面是 null */
  author: string | null;
  name: string;
  mine: boolean;
  written: boolean;
  /** 写了，但你还看不到（互盲） */
  hidden: boolean;
  /** 看得到的字；没写、看不到时是空字符串 */
  text: string;
  createdAt?: string;
  /** 自己这面的版本号：改的时候带上（还没写过是 null） */
  base: string | null;
  voice?: VoiceDto;
}

export interface CardFaces {
  front: CardFace;
  back: CardFace;
  /** 两个人都写了：卡片顶上亮一颗双星，星海里这颗星外面多一圈双光环 */
  resonance: boolean;
  /** 正面右下角翻面按钮上的字 */
  turnLabel: string;
  /** 正面没有能看的字时下面那一行：自己的是邀请（点了写），Ta 的是说明 */
  frontNote: string;
  /** 背面没有能看的字、也不是自己要写的时候，中间那一行 */
  backNote: string;
}

function face(author: string | null, name: string, me: string, list: readonly PerspectiveDto[]): CardFace {
  const p = author ? list.find((x) => x.memberId === author) : undefined;
  return {
    author,
    name,
    mine: author === me,
    written: p !== undefined,
    hidden: p?.hidden === true,
    text: p?.text ?? '',
    createdAt: p?.createdAt,
    base: author === me ? (p?.updatedAt ?? null) : null,
  };
}

export function cardFaces(c: CardInput): CardFaces {
  const nameOf = (id: string) => (id === c.me ? c.myName : (c.partner?.name ?? 'Ta'));
  const other = c.uploader === c.me ? (c.partner?.id ?? null) : c.me;
  const front = face(c.uploader, nameOf(c.uploader), c.me, c.perspectives);
  const back = face(other, other ? nameOf(other) : 'Ta', c.me, c.perspectives);
  front.voice = c.voices?.find((v) => v.memberId === front.author);
  back.voice = c.voices?.find((v) => v.memberId === back.author);

  let turnLabel: string, backNote = '';
  if (back.mine) turnLabel = back.written ? '我的视角 ↻' : '写下我的视角 ↻';
  else if (!back.written && back.voice) {
    turnLabel = 'Ta 的声音 ↻';
    backNote = 'Ta 留下了一段声音';
  } else if (!back.written) {
    turnLabel = '等待 Ta 翻开 ↻';
    backNote = back.author ? '已送出星愿，等待 Ta 回应' : 'Ta 加入星海以后，这一面是 Ta 的视角';
  } else if (back.hidden) {
    turnLabel = 'Ta 写了回响 ↻';
    backNote = 'Ta 已经写好了。写下你在正面的那几句，两边一起揭晓';
  } else turnLabel = 'Ta 的回响 ↻';

  let frontNote = '';
  if (front.mine && !front.written) frontNote = '写下这张照片的心情…';
  else if (front.hidden) frontNote = 'Ta 写了几句。写下你的视角，两边一起揭晓';

  return { front, back, resonance: front.written && back.written, turnLabel, frontNote, backNote };
}

/** 正面署名里的日期：「2024.08.15」（按这台设备的时区） */
export function writtenOn(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}
